import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {EagColumn} from '../../../../../../types/EagColumn';
import type {EagColumnState} from '../../../../../../types/EagColumnState';
import {HighlightedColumnLabel} from '../HighlightedColumnLabel/HighlightedColumnLabel';
import {RevealColumnButton} from '../RevealColumnButton/RevealColumnButton';

export type SortableColumnItemProps = {
  column: EagColumn;
  columnState: EagColumnState;
  /** Group header name shown as secondary text below the label (omitted for ungrouped columns). */
  groupLabel?: string;
  /** Renders the group label in teal for availability columns. */
  isAvailabilityTag?: boolean;
  onSetHidden: (key: string, hidden: boolean) => void;
  columnHidingAllowed: boolean;
  isDragDisabled: boolean;
  /** Highlights the row while it matches the scroll-to-mode search term. */
  isSearchMatch?: boolean;
  /** Stronger highlight for the match the cycle currently points at. */
  isCurrentMatch?: boolean;
  /** Active search term — its occurrence in the label or the group label is <mark>ed. */
  searchTerm?: string;
  /** When set, renders the per-row "scroll to column" action (reveal in grid). */
  onRevealInGrid?: (key: string) => void;
};

function GripIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g fill="currentColor" stroke="none">
        <circle cx="9" cy="6" r="1.5" />
        <circle cx="9" cy="12" r="1.5" />
        <circle cx="9" cy="18" r="1.5" />
        <circle cx="15" cy="6" r="1.5" />
        <circle cx="15" cy="12" r="1.5" />
        <circle cx="15" cy="18" r="1.5" />
      </g>
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" />
      </g>
    </svg>
  );
}

export function SortableColumnItem(props: SortableColumnItemProps) {
  const t = useTranslationContext();
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: props.column.key,
  });

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
  };

  const dragHandleProps = props.isDragDisabled ? {} : {...attributes, ...listeners};
  const dragCursorClass = props.isDragDisabled ? 'cursor-default' : 'cursor-grab';

  const isHideDisabled = !props.columnHidingAllowed || props.column.hideable === false;

  const groupLabelClassName = props.isAvailabilityTag ? 'text-[#035361]' : 'text-[#6B778C]';

  const matchBgClass =
    props.isCurrentMatch === true // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? 'bg-[#B3D4FF]'
      : props.isSearchMatch === true
        ? 'bg-[#DEEBFF]'
        : 'hover:bg-[#FAFBFC]';

  return (
    <div
      ref={setNodeRef}
      style={dragStyle}
      className={`group flex items-center gap-[6px] px-2 py-1 min-h-[34px] rounded-[4px] text-[13px] text-[#091E42] ${matchBgClass} ${dragCursorClass}`}
      data-testid={`columns-panel-shown-${props.column.key}`}
      data-search-match={props.isSearchMatch === true ? 'true' : undefined}
      data-search-current={props.isCurrentMatch === true ? 'true' : undefined}
    >
      <span
        className={`text-[#B3BAC5] flex items-center self-center h-5 flex-shrink-0 ${dragCursorClass}`}
        {...dragHandleProps}
      >
        <GripIcon />
      </span>
      <span className="flex flex-col items-start gap-0.5 flex-1 min-w-0">
        <span
          className="w-full overflow-hidden text-ellipsis whitespace-nowrap font-medium leading-5"
          title={props.column.headerName}
        >
          <HighlightedColumnLabel
            text={props.column.headerName}
            searchTerm={props.searchTerm ?? ''}
          />
        </span>
        <Show when={isNotNilOrEmpty(props.groupLabel) ? props.groupLabel : null}>
          {(groupLabel) => (
            <span
              className={`max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-[11px] leading-4 ${groupLabelClassName}`}
              title={groupLabel}
            >
              <HighlightedColumnLabel text={groupLabel} searchTerm={props.searchTerm ?? ''} />
            </span>
          )}
        </Show>
      </span>
      {/* With the scroll-to feature on (onRevealInGrid set), actions surface on
          row hover or keyboard focus — but ONLY on hover-capable devices; touch
          users (and flag-off grids, matching master) keep them always visible.
          They stay in the DOM (clickable/focusable) either way. */}
      <span
        className={`flex items-center self-center gap-[2px] flex-shrink-0 ${
          props.onRevealInGrid != null
            ? '[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:focus-within:opacity-100'
            : ''
        }`}
      >
        <Show when={props.onRevealInGrid}>
          {(onRevealInGrid) => (
            <RevealColumnButton column={props.column} onReveal={onRevealInGrid} />
          )}
        </Show>
        <button
          type="button"
          className="border-0 bg-transparent cursor-pointer flex rounded-[6px] flex-shrink-0 hover:bg-[#EBECF0] hover:text-[#091E42] disabled:opacity-30 disabled:cursor-not-allowed"
          style={{color: '#7A869A', padding: '3px'}}
          aria-label={t('page.datagrid.labels.hideColumnAria', {column: props.column.headerName})}
          disabled={isHideDisabled}
          onClick={() => props.onSetHidden(props.column.key, true)}
          data-testid={`columns-panel-hide-${props.column.key}`}
        >
          <EyeIcon />
        </button>
      </span>
    </div>
  );
}
